"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const oneEntry_1 = require("../base/oneEntry");
/**
 * Controllers for working with page objects, including catalog pages
 */
class PageApi extends oneEntry_1.default {
    constructor(url, token) {
        super(url, token);
        this._url += '/api/content/pages';
    }
    /**
     * Get all top-level page objects.
     *
     * @param {string | Array<string>} [langCode] Required parameter lang code
     *
     * @returns Returns all created pages without parents as an array of PageEntity objects or an empty array [] (if there is no data)
     */
    async getRootPages(langCode) {
        const result = await this._fetchRequests(`/root?langCode=${this._LANGCODE_KEY}`, langCode);
        return result;
    }
    /**
     * Get all page objects with product information as an array.
     *
     * @param {string | Array<string>} [langCode] Required parameter lang code
     *
     * @param {number} [limit] - Optional parameter for pagination, default is 30
     *
     * @param {number} [offset] - Optional parameter for pagination, default is 0
     *
     * @returns Returns all created pages as an array of PageEntity objects or an empty array [] (if there is no data)
     */
    async getCatalogPages(langCode, limit = 30, offset = 0) {
        const result = await this._fetchRequests(`/catalog?langCode=${this._LANGCODE_KEY}&limit=${limit}&offset=${offset}`, langCode);
        return result;
    }
    /**
     * Get all page objects with product information as an array.
     *
     * @param {string | Array<string>} [langCode] Required parameter lang code
     *
     * @returns Returns all created pages as an array of PageEntity objects or an empty array [] (if there is no data)
     */
    async getPages(langCode) {
        const result = await this._fetchRequests(`?langCode=${this._LANGCODE_KEY}`, langCode);
        return result;
    }
    /**
     * Get page object with information about forms, blocks, menus, linked to the page.
     *
     * @param {string | Array<string>} [langCode] Required parameter lang code
     *
     * @param {number} [id] - Page object identifier
     *
     * @returns Returns PageEntity object
     */
    async getPageById(id, langCode) {
        const result = await this._fetchRequests(`/${id}?langCode=${this._LANGCODE_KEY}`, langCode);
        return result;
    }
    /**
     * Get page object with information about forms, blocks, menus, linked to the page by URL.
     *
     * @param {string | Array<string>} [langCode] Required parameter lang code
     *
     * @param {string} [url] - Page URL
     *
     * @returns Returns PageEntity object
     */
    async getPageByUrl(url, langCode) {
        const result = await this._fetchRequests(`/url/${url}?langCode=${this._LANGCODE_KEY}`, langCode);
        return result;
    }
    /**
     * Get child pages object with information as an array.
     *
     * @param {string | Array<string>} [langCode] Required parameter lang code
     *
     * @param {string} [url] - Page URL
     *
     * @returns Returns all created pages as an array of PageEntity objects or an empty array [] (if there is no data) for the selected parent
     */
    async getChildPagesByParentUrl(url, langCode) {
        const result = await this._fetchRequests(`/${url}/children?langCode=${this._LANGCODE_KEY}`, langCode);
        return result;
    }
    /**
     * Get all forms by page url.
     *
     * @param {string | Array<string>} [langCode] Required parameter lang code
     *
     * @param {string} [url] - Page URL
     *
     * @returns Returns all forms as an array of PositionForm objects or an empty array [] (if there is no data) for the selected parent
     */
    async getFormsByPageUrl(url, langCode) {
        const result = await this._fetchRequests(`/${url}/forms?langCode=${this._LANGCODE_KEY}`, langCode);
        return result;
    }
    /**
     * Get settings for the page.
     *
     * @param {string} [url] - Page URL
     *
     * @returns Returns a ConfigPage object with page display settings
     */
    async getConfigPageByUrl(url) {
        const result = await this._fetchGet(`/${url}/config`);
        return result;
    }
    /**
     * Quick search for page objects with limited output.
     *
     * @param {string} [name] - Text for searching page objects (search is performed on the title field of the localizeInfos object with the language taken into account)
     *
     * @param {string} [langCode] Required parameter lang code
     *
     * @returns Returns all created pages as an array of PageEntity objects or an empty array [] (if there is no data)
     */
    async searchPage(name, langCode) {
        const result = await this._fetchGet(`/quick/search?lang=${langCode}&name=${name}`);
        return result;
    }
}
exports.default = PageApi;
